// @loom/shared/code-map — the Projekt-Karte data contract.
//
// A "Projekt-Karte" is a server-stored, per-project button tree that lets a
// non-technical author point at WHERE in the code their request belongs by
// drilling down through labelled buttons instead of answering the free-text
// "Wo im Code gehört das hin?" question. The tree is a FLAT node list — the
// same "surfaces spine" shape the generation experiment emits — so the whole
// map is one jsonb blob and the parent/child structure is derived, not nested:
//
//   { id, parent, label, label_en, description, code_paths[] }   (parent=null → root)
//
// This module is the single source of truth for that shape: the Zod schemas
// (server validates upserts + the wire response, web parses the fetch), the
// pure tree helpers both surfaces render from, and the self-labelled answer
// builder. Consuming it from ONE place keeps the server gate, the client panel,
// and the seed prose byte-consistent — the grounding gate quote-scans the exact
// answer string this builder emits.

import { z } from "zod";

/** Breadcrumb / answer separator (U+203A, single right angle quote). */
export const PATH_SEP = " › ";

/**
 * Self-label prefix for a chosen location, mirroring the widget answer
 * convention (`Milestone: …`, `Priority: …`). The leaf answer travels as a
 * quotable user turn shaped `Ort: A › B › C`; the grounding gate's
 * normalized-substring scan finds it verbatim.
 */
export const LOCATION_ANSWER_PREFIX = "Ort";

/**
 * Fixed ceiling of buttons per level. It is a DATA property (the map generator
 * keeps every level ≤ 6 so the panel never overwhelms), validated at
 * provisioning — NOT a UI invariant. The panel still slices defensively.
 */
export const MAX_CHILDREN_PER_LEVEL = 6;

export const codeMapNodeSchema = z.object({
  /** Stable slug, unique within the tree. */
  id: z.string().min(1),
  /** Parent node id, or `null` for a root button. */
  parent: z.string().min(1).nullable(),
  /** Primary display label (project content locale). */
  label: z.string().min(1),
  /** English label — advisory / reference; may be empty. */
  label_en: z.string(),
  /** One-line description rendered under the button; may be empty. */
  description: z.string(),
  /**
   * English description, paired with `label_en` so an English-locale viewer
   * gets a whole node in one language rather than an English heading over a
   * German sentence. OPTIONAL on purpose: maps generated before this field
   * existed must still parse — readers fall back to `description`.
   */
  description_en: z.string().optional(),
  /**
   * Advisory code pointers that ride invisibly with a LEAF choice. Empty for
   * interior nodes. These are hints for the coding agent, never a hard fence.
   */
  code_paths: z.array(z.string()),
});
export type CodeMapNode = z.infer<typeof codeMapNodeSchema>;

export const codeMapTreeSchema = z.object({
  nodes: z.array(codeMapNodeSchema),
});
export type CodeMapTree = z.infer<typeof codeMapTreeSchema>;

/**
 * Wire response from `GET /api/projects/:id/code-map`. The discriminant lets
 * the client cleanly tell "this project has no map → skip the location step"
 * apart from "this project has a map → render the panel".
 */
export const projectCodeMapResponseSchema = z.discriminatedUnion("hasMap", [
  z.object({ hasMap: z.literal(false) }),
  z.object({
    hasMap: z.literal(true),
    version: z.number().int().nonnegative(),
    tree: codeMapTreeSchema,
  }),
]);
export type ProjectCodeMapResponse = z.infer<
  typeof projectCodeMapResponseSchema
>;

/** Body shape for the operator upsert (dev-internal route + seed script). */
export const projectCodeMapUpsertSchema = z.object({
  projectId: z.number().int().positive(),
  version: z.number().int().nonnegative(),
  defaultBranchSha: z.string().nullable().optional(),
  tree: codeMapTreeSchema,
  /** ISO-8601 timestamp the map was generated; defaults to now on the server. */
  generatedAt: z.string().datetime().optional(),
});
export type ProjectCodeMapUpsert = z.infer<typeof projectCodeMapUpsertSchema>;

/* ─── Pure tree helpers (shared by the panel + the server) ─────────────── */

/** Direct children of `parentId` (`null` yields the roots), in list order. */
export const childrenOf = (
  tree: CodeMapTree,
  parentId: string | null,
): CodeMapNode[] => tree.nodes.filter((n) => n.parent === parentId);

/** The node with `id`, or `undefined`. */
export const nodeById = (
  tree: CodeMapTree,
  id: string,
): CodeMapNode | undefined => tree.nodes.find((n) => n.id === id);

/** Root buttons (`parent === null`). */
export const rootNodes = (tree: CodeMapTree): CodeMapNode[] =>
  childrenOf(tree, null);

/** A node is a leaf when nothing lists it as a parent. */
export const isLeaf = (tree: CodeMapTree, id: string): boolean =>
  childrenOf(tree, id).length === 0;

/**
 * Resolve a drill path (node ids) to their CANONICAL labels.
 *
 * These are the bytes `buildLocationAnswer` embeds and the grounding gate
 * later scans for, so they must never vary by viewer locale. Use
 * `nodeDisplayLabel` / `pathDisplayLabels` for anything shown on screen.
 */
export const pathLabels = (tree: CodeMapTree, path: string[]): string[] =>
  path.map((id) => nodeById(tree, id)?.label ?? id);

/**
 * The label to SHOW a viewer. `label` is written in the project's own content
 * language (the generator infers it from the README, defaulting to German);
 * `label_en` is the English rendering it always fills in alongside. An
 * English-locale viewer gets `label_en`, everyone else the canonical `label`.
 *
 * Display only — never feed this into `buildLocationAnswer`, or the grounding
 * scan stops matching the stored answer.
 */
export const nodeDisplayLabel = (node: CodeMapNode, locale: string): string =>
  locale.startsWith("en") && node.label_en.trim() !== ""
    ? node.label_en
    : node.label;

/**
 * The description to SHOW a viewer. Same rule as `nodeDisplayLabel`, with a
 * fallback to the canonical `description` for maps generated before
 * `description_en` existed.
 */
export const nodeDisplayDescription = (
  node: CodeMapNode,
  locale: string,
): string =>
  locale.startsWith("en") && (node.description_en ?? "").trim() !== ""
    ? (node.description_en as string)
    : node.description;

/** `pathLabels`, localized for display. */
export const pathDisplayLabels = (
  tree: CodeMapTree,
  path: string[],
  locale: string,
): string[] =>
  path.map((id) => {
    const node = nodeById(tree, id);
    return node ? nodeDisplayLabel(node, locale) : id;
  });

/**
 * The self-labelled, quotable answer for a chosen label path, e.g.
 * `Ort: Zirkular-Tool › Bauteile verwalten`. This exact string is what the
 * create seed embeds and the grounding gate later scans for.
 */
export const buildLocationAnswer = (labels: string[]): string =>
  `${LOCATION_ANSWER_PREFIX}: ${labels.join(PATH_SEP)}`;

/**
 * The LEAF segment of a path string — either a `buildLocationAnswer` answer
 * (`Ort: A › B › C` → `C`) or a bare joined path (`A › B › C` → `C`).
 *
 * Read-only: it never rewrites the answer, so the bytes the grounding gate
 * scans for are untouched. It exists so the create seed can NAME the picked
 * area in both languages — the map's canonical label is what the seed embeds
 * (and must keep embedding), but it is also the only name the model ever
 * sees, which is why an English session got German acceptance criteria.
 */
export const locationAnswerLeaf = (path: string): string => {
  const prefix = `${LOCATION_ANSWER_PREFIX}: `;
  const body = path.startsWith(prefix) ? path.slice(prefix.length) : path;
  const parts = body.split(PATH_SEP);
  return (parts[parts.length - 1] ?? "").trim();
};

export interface TreeValidation {
  readonly ok: boolean;
  readonly reason?: string;
}

/**
 * Defensive structural check for a code map: unique ids, resolvable parents,
 * ≤ {@link MAX_CHILDREN_PER_LEVEL} per level, at least one root, no cycles.
 * Run at provisioning (reject a malformed upload) and client-side (fall back
 * to the free-text step rather than render a broken panel). An empty tree is
 * treated as invalid — a provisioned map must have content.
 */
export const validateCodeMapTree = (tree: CodeMapTree): TreeValidation => {
  if (tree.nodes.length === 0) return { ok: false, reason: "empty tree" };

  const ids = new Set<string>();
  for (const n of tree.nodes) {
    if (ids.has(n.id)) return { ok: false, reason: `duplicate id: ${n.id}` };
    ids.add(n.id);
  }
  for (const n of tree.nodes) {
    if (n.parent !== null && !ids.has(n.parent)) {
      return { ok: false, reason: `node ${n.id} has unknown parent ${n.parent}` };
    }
  }

  const counts = new Map<string | null, number>();
  for (const n of tree.nodes) {
    counts.set(n.parent, (counts.get(n.parent) ?? 0) + 1);
  }
  for (const [parent, count] of counts) {
    if (count > MAX_CHILDREN_PER_LEVEL) {
      return {
        ok: false,
        reason: `${parent ?? "root"} has ${count} children (max ${MAX_CHILDREN_PER_LEVEL})`,
      };
    }
  }

  if (rootNodes(tree).length === 0) {
    return { ok: false, reason: "no root node (parent=null)" };
  }

  // Every node must reach a root by walking parents — catches cycles and
  // orphan chains that dangle off a missing ancestor.
  for (const start of tree.nodes) {
    const seen = new Set<string>();
    let cur: CodeMapNode | undefined = start;
    while (cur && cur.parent !== null) {
      if (seen.has(cur.id)) {
        return { ok: false, reason: `cycle through ${cur.id}` };
      }
      seen.add(cur.id);
      cur = nodeById(tree, cur.parent);
    }
    if (!cur) return { ok: false, reason: `${start.id} does not reach a root` };
  }

  return { ok: true };
};
